import { ComponentProps, useCallback, useState } from 'react';
import QRCode from 'qrcode';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';

interface QrCodeDownloadButtonProps {
  url: string;
  filename?: string;
  format?: 'png' | 'jpg';
  buttonProps?: ComponentProps<typeof Button>;
}

/**
 * Component that generates and downloads a QR code for the given URL.
 * The QR code is generated on-demand when the button is clicked.
 */
export function QrCodeDownloadButton({ url, filename, format = 'png', buttonProps = {} }: QrCodeDownloadButtonProps) {
  const { toast } = useToast();
  const { className, ...rest } = buttonProps;
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = useCallback(async () => {
    setIsLoading(true);
    let blobUrl: string | null = null;

    try {
      // Generate QR code as data URL (always generate as PNG first for quality)
      const pngDataUrl = await QRCode.toDataURL(url, {
        width: 512,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });

      let blob: Blob;
      const finalFilename = filename || `qrcode-${Date.now()}.${format}`;

      if (format === 'jpg') {
        // Convert PNG to JPG by drawing on canvas
        const img = new Image();
        img.src = pngDataUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        // Fill white background for JPG (since JPG doesn't support transparency)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        // Convert canvas to JPG blob
        blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to convert canvas to blob'));
              }
            },
            'image/jpeg',
            0.95, // Quality
          );
        });
      } else {
        // Convert data URI to blob directly (without fetch to avoid CSP violations)
        const base64Data = pngDataUrl.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: 'image/png' });
      }

      // Create download link
      blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = finalFilename;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'QR code downloaded',
      });
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      toast({
        title: 'Failed to download QR code',
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      // Clean up blob URL
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
      setIsLoading(false);
    }
  }, [url, filename, format, toast]);

  return (
    <Button
      type="button"
      icon={Download}
      onClick={handleDownload}
      size="sm"
      variant="ghost"
      loading={isLoading}
      className={className}
      {...rest}
    />
  );
}
